import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Home Services", "Professional Services", "Healthcare", "Retail / E-commerce",
  "Real Estate", "Restaurant / Food", "Technology", "Education", "Automotive", "Other"
];

export default function LeaderboardOptIn({ leadId, overallScore }) {
  const [listed, setListed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ business_name: "", industry: "Other", city: "Sacramento" });

  useEffect(() => {
    if (!leadId) { setChecking(false); return; }
    const check = async () => {
      try {
        const res = await axios.get(`${API}/leaderboard/check`, { params: { lead_id: leadId } });
        setListed(res.data.listed);
      } catch {
        // ignore
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [leadId]);

  const handleSubmit = async () => {
    if (!form.business_name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leaderboard/opt-in`, {
        lead_id: leadId,
        business_name: form.business_name,
        industry: form.industry,
        city: form.city,
      });
      setListed(true);
      setShowForm(false);
    } catch (e) {
      console.error("Leaderboard opt-in failed:", e);
    } finally {
      setLoading(false);
    }
  };

  if (checking) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="mb-10" data-testid="leaderboard-optin-section">

      <AnimatePresence mode="wait">
        {listed ? (
          <motion.div key="listed" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="border border-yellow-400/20 rounded-xl p-6 bg-yellow-400/5">
            <div className="flex items-center gap-3">
              <Trophy className="w-6 h-6 text-yellow-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-zinc-300">You're on the leaderboard!</p>
                <p className="text-xs text-zinc-500">Your business is now visible to other users.</p>
              </div>
              <a href="/leaderboard" className="flex items-center gap-1 text-sm text-orange-500 hover:text-orange-400 transition-colors font-semibold"
                data-testid="view-leaderboard-link">
                View Leaderboard <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        ) : !showForm ? (
          <motion.div key="prompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="border border-zinc-800 rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-yellow-400/5 to-transparent p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-400/10 border border-yellow-400/20 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Claim Your Spot on the Leaderboard
                </h3>
                <p className="text-sm text-zinc-400 mt-0.5">
                  Show off your visibility score! Join the top businesses in your area.
                  {overallScore >= 60 && <span className="text-green-400 font-semibold"> Your score qualifies for the top ranks!</span>}
                </p>
              </div>
              <button onClick={() => setShowForm(true)}
                className="flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 font-semibold py-2.5 px-5 rounded-md hover:bg-yellow-400/20 transition-colors whitespace-nowrap"
                data-testid="claim-spot-button">
                <Trophy className="w-4 h-4" /> Claim My Spot
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="border border-yellow-400/20 rounded-xl p-6 bg-[#1a1a1c]/50">
            <h3 className="text-base font-bold text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Add Your Business to the Leaderboard
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1.5 block">Business Name</label>
                <input type="text" placeholder="Your Business Name"
                  value={form.business_name} onChange={e => setForm(p => ({ ...p, business_name: e.target.value }))}
                  className="w-full bg-[#0f0f10] border border-zinc-700 focus:border-orange-500 rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-orange-500/30 transition-colors"
                  data-testid="leaderboard-business-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1.5 block">Industry</label>
                  <select value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                    className="w-full bg-[#0f0f10] border border-zinc-700 focus:border-orange-500 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-orange-500/30 transition-colors"
                    data-testid="leaderboard-industry-select">
                    {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1.5 block">City</label>
                  <input type="text" placeholder="Sacramento"
                    value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                    className="w-full bg-[#0f0f10] border border-zinc-700 focus:border-orange-500 rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-orange-500/30 transition-colors"
                    data-testid="leaderboard-city-input" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button onClick={handleSubmit} disabled={loading || !form.business_name.trim()}
                  className="flex items-center gap-2 bg-orange-500 text-white font-bold py-2.5 px-6 rounded-md transition-all hover:bg-orange-600 shadow-[0_0_15px_rgba(249,115,22,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="submit-leaderboard-button">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</> : <><CheckCircle2 className="w-4 h-4" /> Add to Leaderboard</>}
                </button>
                <button onClick={() => setShowForm(false)} className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors">Cancel</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
